import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Office, OfficeAnalytics, Page, SlotsResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatCard } from '../../ui/components';

function addDaysISO(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Buckets a flat list of ISO slot instants into per-calendar-day open-slot counts. */
function bucketByDate(slots: string[]): Array<{ date: string; count: number }> {
  const counts = new Map<string, number>();
  for (const s of slots) {
    const day = s.slice(0, 10);
    counts.set(day, (counts.get(day) ?? 0) + 1);
  }
  return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, count]) => ({ date, count }));
}

export function Dashboard() {
  const { persona } = useAuth();
  const isManager = persona?.roles.includes('OFFICE_MANAGER') ?? false;
  const officesQ = useApiQuery<Page<Office>>('/api/v1/servicedesk/offices?limit=100');
  const offices = useMemo(() => officesQ.data?.items ?? [], [officesQ.data]);
  const [officeCode, setOfficeCode] = useState('');

  useEffect(() => {
    if (officeCode || offices.length === 0) return;
    const preferred =
      persona?.officeCode && offices.some((o) => o.code === persona.officeCode) ? persona.officeCode : offices[0]!.code;
    setOfficeCode(preferred);
  }, [offices, officeCode, persona?.officeCode]);

  const office = offices.find((o) => o.code === officeCode) ?? null;

  const analyticsQ = useApiQuery<OfficeAnalytics>(
    isManager && officeCode ? `/api/v1/servicedesk/offices/${officeCode}/analytics` : null,
  );

  return (
    <div>
      <PageHeader
        title="Manager Dashboard"
        subtitle="Real analytics computed from today's appointments and queue tickets."
        actions={
          <label className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-slate-500">Office</span>
            <select className="field-input !w-auto" value={officeCode} onChange={(e) => setOfficeCode(e.target.value)}>
              {offices.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.name} ({o.code})
                </option>
              ))}
            </select>
          </label>
        }
      />

      {officesQ.error ? <ErrorNote message={officesQ.error} /> : null}
      {officesQ.loading ? <Spinner label="Loading offices…" /> : null}

      {!isManager ? (
        <div className="mb-4">
          <ErrorNote message="Office analytics require the Office Manager role — showing office information only." />
        </div>
      ) : null}

      {office ? (
        <>
          {isManager ? (
            <div className="mb-6 grid gap-3 sm:grid-cols-3">
              {analyticsQ.loading ? (
                <div className="sm:col-span-3">
                  <Spinner label="Computing analytics…" />
                </div>
              ) : analyticsQ.error ? (
                <div className="sm:col-span-3">
                  <ErrorNote message={analyticsQ.error} />
                </div>
              ) : analyticsQ.data ? (
                <>
                  <StatCard label="Appointments today" value={analyticsQ.data.appointmentsToday} hint={analyticsQ.data.date} />
                  <StatCard
                    label="No-show rate"
                    value={`${Math.round(analyticsQ.data.noShowRate * 100)}%`}
                    tone={analyticsQ.data.noShowRate > 0.2 ? 'bad' : analyticsQ.data.noShowRate > 0.1 ? 'warn' : 'good'}
                  />
                  <StatCard
                    label="Average wait"
                    value={analyticsQ.data.averageWaitMinutes !== null ? `${analyticsQ.data.averageWaitMinutes} min` : '—'}
                    hint={analyticsQ.data.averageWaitMinutes === null ? 'No queue tickets served yet today' : 'Draw-to-served, walk-in queue'}
                  />
                </>
              ) : null}
            </div>
          ) : null}

          <div className="mb-6 grid gap-5 lg:grid-cols-2">
            <div className="card p-5">
              <h2 className="mb-2 text-sm font-bold text-slate-700">Office</h2>
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Island / district</dt>
                  <dd className="font-medium text-slate-800">
                    {office.island} · {office.district}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Counters</dt>
                  <dd className="font-medium text-slate-800">{office.counters}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Services offered</dt>
                  <dd className="font-medium text-slate-800">{office.services.join(', ') || '—'}</dd>
                </div>
              </dl>
              <div className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <div className="mb-1 font-semibold uppercase tracking-wide text-slate-400">Opening hours</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((d) => (
                    <div key={d} className="flex justify-between">
                      <span className="uppercase">{d}</span>
                      <span className="tabular-nums">{office.hours[d] ?? 'closed'}</span>
                    </div>
                  ))}
                </div>
              </div>
              <a
                href={`/tv/${office.code}`}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary mt-4 inline-flex"
              >
                Open queue display (TV) ↗
              </a>
            </div>

            <SlotOverview office={office} />
          </div>
        </>
      ) : null}
    </div>
  );
}

function SlotOverview({ office }: { office: Office }) {
  const [serviceCode, setServiceCode] = useState(office.services[0] ?? '');
  const [buckets, setBuckets] = useState<Array<{ date: string; count: number }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async (code: string) => {
    if (!code) {
      setBuckets(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<SlotsResponse>(
        `/api/v1/servicedesk/appointments/slots?officeCode=${office.code}&serviceCode=${code}&from=${addDaysISO(0)}&to=${addDaysISO(6)}`,
      );
      setBuckets(bucketByDate(res.slots));
    } catch (e) {
      setBuckets(null);
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not load slot availability');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(serviceCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCode, office.code]);

  const max = Math.max(1, ...(buckets ?? []).map((b) => b.count));

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Open appointment slots — next 7 days</h2>
        <Field label="" className="!mb-0">
          <select className="field-input !w-auto !py-1" value={serviceCode} onChange={(e) => setServiceCode(e.target.value)}>
            {office.services.length === 0 ? <option value="">No services</option> : null}
            {office.services.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {loading ? (
        <Spinner label="Loading availability…" />
      ) : !buckets || buckets.length === 0 ? (
        <EmptyState title="No open slots in the next 7 days" hint="Every opening for this service is either booked or the office is closed those days." />
      ) : (
        <div className="flex h-40 items-end gap-2">
          {buckets.map((b) => (
            <div key={b.date} className="flex flex-1 flex-col items-center gap-1">
              <div className="text-[11px] font-semibold tabular-nums text-slate-600">{b.count}</div>
              <div
                className="w-full rounded-t bg-brand-500"
                style={{ height: `${Math.max(6, (b.count / max) * 100)}%` }}
                title={`${b.count} open slots`}
              />
              <div className="text-[10px] text-slate-400">
                {new Date(b.date).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
